class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('submit', this.onSubmit.bind(this));
    // Remove buttons also sit on the line-level discount rows, which live in
    // the items section outside this element — so the click listener has to
    // be on the document. Only one cart-discount exists per page, and
    // refreshSections replaces its innerHTML rather than the element, so this
    // can neither double-bind nor double-fire.
    document.addEventListener('click', this.onClick.bind(this));
  }

  onSubmit(event) {
    if (!event.target.matches('form')) return;
    event.preventDefault();
    const input = this.querySelector('input[name="discount-code"]');
    const code = (input?.value || '').trim();
    if (!code) return;

    const existing = this.getCurrentCodes();
    if (existing.some((c) => c.toLowerCase() === code.toLowerCase())) {
      input.value = '';
      return;
    }
    this.updateCodes([...existing, code], code);
  }

  onClick(event) {
    const btn = event.target.closest('[data-discount-remove]');
    if (!btn) return;
    event.preventDefault();
    const code = btn.dataset.discountRemove;
    const remaining = this.getCurrentCodes().filter(
      (c) => c.toLowerCase() !== code.toLowerCase()
    );
    this.updateCodes(remaining);
  }

  getCurrentCodes() {
    return Array.from(this.querySelectorAll('[data-discount-code]')).map(
      (el) => el.dataset.discountCode
    );
  }

  getSectionsToRender() {
    const sections = [];
    const footer = document.getElementById('main-cart-footer');
    if (footer) {
      sections.push({
        id: 'main-cart-footer',
        section: footer.dataset.id,
        selector: '.js-contents',
      });
    }
    const items = document.getElementById('main-cart-items');
    if (items) {
      sections.push({
        id: 'main-cart-items',
        section: items.dataset.id,
        selector: '.js-contents',
      });
    }
    sections.push({
      id: 'cart-icon-bubble',
      section: 'cart-icon-bubble',
      selector: '.shopify-section',
    });
    return sections;
  }

  setLoading(loading) {
    this.classList.toggle('cart-discount--loading', loading);
    this.querySelectorAll('button, input').forEach((el) => {
      el.disabled = loading;
    });
  }

  clearError() {
    const errorEl = this.querySelector('.cart-discount__error');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.hidden = true;
    }
  }

  showError(message) {
    const errorEl = this.querySelector('.cart-discount__error');
    if (errorEl) {
      errorEl.textContent = message;
      errorEl.hidden = false;
    }
  }

  updateCodes(codes, attemptedCode) {
    this.setLoading(true);
    this.clearError();

    const sections = this.getSectionsToRender();
    const body = JSON.stringify({
      discount: codes.join(','),
      sections: sections.map((s) => s.section),
      sections_url: window.location.pathname,
    });

    fetch(`${window.routes?.cart_update_url || '/cart/update.js'}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status && data.status >= 400) {
          this.showError(
            data.description || data.message || 'Could not apply discount code.'
          );
          return;
        }

        if (attemptedCode) {
          const entry = (data.discount_codes || []).find(
            (d) => d.code.toLowerCase() === attemptedCode.toLowerCase()
          );
          if (!entry || !entry.applicable) {
            // Refresh first: it replaces this element's innerHTML, which would
            // otherwise wipe the message before anyone could read it.
            this.refreshSections(data, sections);
            this.showError(
              entry
                ? 'This code is not applicable to your cart.'
                : 'Discount code not found.'
            );
            return;
          }
        }

        const input = this.querySelector('input[name="discount-code"]');
        if (input) input.value = '';
        this.refreshSections(data, sections);
      })
      .catch((err) => {
        console.error(err);
        this.showError('Something went wrong. Please try again.');
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  refreshSections(data, sections) {
    if (!data.sections) return;

    sections.forEach((section) => {
      const html = data.sections[section.section];
      if (!html) return;
      const container = document.getElementById(section.id);
      if (!container) return;
      const target =
        container.querySelector(section.selector) || container;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const source =
        parsed.querySelector(section.selector) || parsed.body;
      if (source) target.innerHTML = source.innerHTML;
    });

    const footerHtml = data.sections[
      document.getElementById('main-cart-footer')?.dataset.id
    ];
    if (footerHtml) {
      const parsed = new DOMParser().parseFromString(footerHtml, 'text/html');
      const fresh = parsed.querySelector('cart-discount');
      if (fresh) this.innerHTML = fresh.innerHTML;
    }

    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-discount',
        cartData: data,
      });
    }
  }
}

customElements.define('cart-discount', CartDiscount);
